/**
 * Configuration and Brand API Service
 * Handles fetching configurations and brands from backend API
 */

import authService from './authService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const getAuthHeaders = async () => {
  const token = await authService.getToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

class ConfigAPI {
  /**
   * Get list of configurations for authenticated user
   */
  static async getConfigurations() {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/configurations`, {
        headers
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch configurations: ${response.statusText}`);
      }
      return response.json();
    } catch (error) {
      console.error('ConfigAPI.getConfigurations error:', error);
      throw error;
    }
  }

  /**
   * Get specific configuration by ID (returns full config.json structure from JSONB)
   */
  static async getConfiguration(configId) {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/configuration/${configId}`, {
        headers
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch configuration: ${response.statusText}`);
      }
      return response.json();
    } catch (error) {
      console.error('ConfigAPI.getConfiguration error:', error);
      throw error;
    }
  }

  /**
   * Get list of brands for authenticated user (includes default flag)
   */
  static async getBrands() {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/brands`, {
        headers
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch brands: ${response.statusText}`);
      }
      return response.json();
    } catch (error) {
      console.error('ConfigAPI.getBrands error:', error);
      throw error;
    }
  }

  /**
   * Get specific brand by ID (returns full config.json structure from JSONB)
   */
  static async getBrand(brandId) {
    try {
      console.log('🔍 Fetching brand:', brandId, 'Type:', typeof brandId, 'from', `${API_BASE_URL}/api/v1/brand/${brandId}`);
      const headers = await getAuthHeaders();
      console.log('🔑 Auth headers prepared');
      const response = await fetch(`${API_BASE_URL}/api/v1/brand/${brandId}`, {
        headers
      });
      console.log('📥 Brand response status:', response.status, response.statusText);
      if (!response.ok) {
        let errorText = '';
        try {
          const errorData = await response.json();
          errorText = JSON.stringify(errorData);
          console.error('❌ Brand fetch failed:', response.status, errorData);
        } catch {
          errorText = await response.text();
          console.error('❌ Brand fetch failed:', response.status, errorText);
        }
        throw new Error(`Failed to fetch brand (${response.status}): ${errorText || response.statusText}`);
      }
      const data = await response.json();
      console.log('✅ Brand data received:', data);
      return data;
    } catch (error) {
      console.error('ConfigAPI.getBrand error:', error);
      throw error;
    }
  }

  // Legacy methods for backward compatibility
  static async listConfigurations() {
    return this.getConfigurations();
  }

  static async listBrands() {
    return this.getBrands();
  }
}

export default ConfigAPI;
